import React, { useState, useCallback, useEffect } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker";
import "../../components/DateTimePicker/dateTimePicker.css";
import "../../components/DateTimePicker/materialUIOverrides.css";
import moment from "moment";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import BranchAssignmentSelector from "../../components/CustomComponents/BranchAssignment/BranchAssignmentSelector";

const CreateDeal = () => {     
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const hasProfileData = !!profileData;
  const isChildBranch = useIsChildBranch();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const [compCurrency, setCompCurrency] = useState("GBP");

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [formState, setFormState] = useState({
    dealName: "",
    dealDescription: "",
    dealPrice: "",
    dealOrder: "0",
    dealPricingType: "CustomPrice",
    dealDiscountPercentage: "",
    startDate: null,
    endDate: null,
  });

  const [errors, setErrors] = useState({
    dealName: "",
    dealPrice: "",
  });

  const [toggles, setToggles] = useState({
    isActive: true,
    showInKiosk: false,
    showInPOS: true,
    showInOnline: true,
  });

  const [imageState, setImageState] = useState({
    dealImage: "",
    imageFile: null,
  });

  const [loading, setLoading] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [showBranchModal, setShowBranchModal] = useState(false);

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  useEffect(() => {
    if (!hasProfileData) return;
    if (isChildBranch) {
      showErrorToast("Branch companies cannot create deals.");
      navigate("/deal/manageDeals", { replace: true });
    }
  }, [isChildBranch, navigate, hasProfileData]);

  useEffect(() => {
    if (profileData) {
      setCompCurrency(profileData?.compCurrency || "GBP");
      setCreatedBy(profileData?.userName || "");
    }
  }, [profileData?.compCurrency, profileData?.userName]);

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleToggleChange = (field, value) => {
    setToggles((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (imageUrl, imageFile) => {
    setImageState({
      dealImage: imageUrl,
      imageFile: imageFile,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formState.dealName.trim()) {
      newErrors.dealName = "Deal name is required";
    }

    // When Custom Price is selected, deal price is required and must be >= 0
    if (formState.dealPricingType === "CustomPrice") {
      if (!formState.dealPrice || formState.dealPrice.toString().trim() === "") {
        newErrors.dealPrice = "Deal price is required for Custom Price";
      } else if (parseFloat(formState.dealPrice) < 0) {
        newErrors.dealPrice = "Deal price cannot be negative";
      }
    } else if (formState.dealPrice && parseFloat(formState.dealPrice) < 0) {
      newErrors.dealPrice = "Deal price cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      showErrorToast("Please fix the errors in the form");
      return;
    }

    try {
      setLoading(true);

      // Upload image if there's a new file
      let imageUrl = imageState.dealImage;
      const baseURL = axiosInstance.defaults.baseURL || "";
      try {
        if (imageState.imageFile) {
          const imageFormData = new FormData();
          imageFormData.append("ImageFile", imageState.imageFile);
          const imageResponse = await axiosInstance.post(
            APIs.POST_UPLOAD_PRODUCT_IMAGE,
            imageFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          const relativePath = imageResponse.data?.data?.filePath;
          if (relativePath) {
            // Store relative path in database (backend will add baseURL when fetching)
            imageUrl = relativePath;
          }
        } else if (imageState.dealImage && !imageState.dealImage.startsWith("http")) {
          // If image exists but is not a full URL, ensure it's a relative path
          // Remove base URL if it was accidentally included
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          imageUrl = imageState.dealImage.replace(baseUrlClean, "").replace(/^\/+/, "");
        }
      } catch (err) {
        showErrorToast("Failed to upload image");
      }

      // Format dates properly for ASP.NET Core - use ISO format with T separator
      let formattedStartDate = null;
      let formattedEndDate = null;
      
      if (formState.startDate) {
        // Handle both Date objects and moment objects
        const startDate = formState.startDate instanceof Date 
          ? formState.startDate 
          : formState.startDate instanceof moment 
            ? formState.startDate.toDate() 
            : new Date(formState.startDate);
        
        const startMoment = moment(startDate);
        if (startMoment.isValid()) {
          // Format as ISO 8601: YYYY-MM-DDTHH:mm:ss (ASP.NET Core expects this format)
          formattedStartDate = startMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }
      
      if (formState.endDate) {
        // Handle both Date objects and moment objects
        const endDate = formState.endDate instanceof Date 
          ? formState.endDate 
          : formState.endDate instanceof moment 
            ? formState.endDate.toDate() 
            : new Date(formState.endDate);
        
        const endMoment = moment(endDate);
        if (endMoment.isValid()) {
          // Format as ISO 8601: YYYY-MM-DDTHH:mm:ss (ASP.NET Core expects this format)
          formattedEndDate = endMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }

      const pricingType = (formState.dealPricingType || "").trim();
      const dealData = {
        dealID: 0,
        compID: CompID,
        dealName: formState.dealName.trim(),
        dealDescription: formState.dealDescription.trim() || null,
        dealImage: imageUrl || null,
        dealPrice: pricingType === "CustomPrice" && formState.dealPrice ? parseFloat(formState.dealPrice) : null,
        isActive: toggles.isActive,
        showInKiosk: toggles.showInKiosk,
        showInPOS: toggles.showInPOS,
        showInOnline: toggles.showInOnline,
        dealOrder: parseInt(formState.dealOrder) || 0,
        pricingType: formState.dealPricingType || null,
        discountPercentage: pricingType === "Discount" && formState.dealDiscountPercentage ? parseFloat(formState.dealDiscountPercentage) : null,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        createdBy: createdBy,
        modifiedBy: null,
        branchCompIDs:
          selectedBranches.length > 0
            ? selectedBranches
                .map((b) => {
                  if (typeof b === "number") return b;
                  return b.CompID ?? b.compID ?? b.branchCompID;
                })
                .filter((id) => id != null)
            : null,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL, dealData);

      if (response.data.status) {
        showSuccessToast("Deal created successfully!");
        navigate(`/deal/updateDeal/${response.data.data.dealID}`);
      } else {
        showErrorToast(response.data.message || "Failed to create deal");
      }
    } catch (error) {
      console.error("Error creating deal:", error);
      showErrorToast("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', minHeight: '100vh', backgroundColor: isDarkMode ? '#1a1d29' : '#f5f7fa' }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb mb-2" style={{ backgroundColor: 'transparent', padding: 0 }}>
            <li className="breadcrumb-item">
              <a href="/" style={{ color: isDarkMode ? '#a0aec0' : '#6c757d', textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); navigate("/"); }}>Home</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/deal/manageDeals" style={{ color: isDarkMode ? '#a0aec0' : '#6c757d', textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); navigate("/deal/manageDeals"); }}>Manage Deals</a>
            </li>
            <li className="breadcrumb-item active" style={{ color: themeColor }}>Create Deal</li>
          </ol>
          <h1 className="mb-0" style={{ 
            fontSize: '28px', 
            fontWeight: '700',
            color: isDarkMode ? '#ffffff' : '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <i className="fa fa-tags" style={{ color: themeColor }}></i>
            Create Deal
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <button
            type="button"
            className="btn"
            onClick={() => navigate("/deal/manageDeals")}
            style={{
              background: isDarkMode ? '#4a5568' : '#e2e8f0',
              color: isDarkMode ? '#ffffff' : '#2d3748',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 24px',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDarkMode ? '#5a6678' : '#d2d8e0';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDarkMode ? '#4a5568' : '#e2e8f0';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-deal-form"
            className="btn"
            disabled={loading}
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '10px',
              padding: '10px 24px',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 4px 12px ${themeColor}40`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              height: '44px'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 6px 16px ${themeColor}60`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}40`;
              }
            }}
          >
            <i className="fa fa-save"></i>
            {loading ? 'Creating...' : 'Create Deal'}
          </button>
        </div>
      </div>

      <GridSectionLoader loading={loading} minHeight="400px">
      <form
        id="create-deal-form"
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          // Prevent Enter in inputs from accidentally submitting / leaving the page
          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
            e.preventDefault();
          }
        }}
      >
        <div 
          className="card border-0 shadow-sm mb-4"
          style={{
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : undefined,
            borderRadius: '16px',
            overflow: 'hidden'
          }}
        >
          <div 
            className="card-header border-0"
            style={{
              backgroundColor: isDarkMode ? '#2d3436' : '#f7fafc',
              padding: '20px 24px',
              borderBottom: `2px solid ${isDarkMode ? '#404040' : '#e2e8f0'}`
            }}
          >
            <h3 className="mb-0" style={{ 
              fontSize: '20px', 
              fontWeight: '600',
              color: isDarkMode ? '#ffffff' : '#2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fa fa-info-circle" style={{ color: themeColor }}></i>
              Deal Information
            </h3>
          </div>
          
          <div className="card-body" style={{ padding: '24px' }}>
            <div className="row mb-4 align-items-center">
              <div className="col-md-3 text-center">
                <ImageUpload
                  imageUrl={imageState.dealImage}
                  imageFile={imageState.imageFile}
                  setImage={(url, file) =>
                    setImageState({ dealImage: url, imageFile: file })
                  }
                />
              </div>
              <div className="col-md-9">
                <div className="row row-cols-2 g-3">
                  <div className="col">
                    <ToggleSwitch
                      label="Is Active"
                      isChecked={toggles.isActive}
                      onToggle={(checked) => handleToggleChange("isActive", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col">
                    <ToggleSwitch
                      label="Show in Kiosk"
                      isChecked={toggles.showInKiosk}
                      onToggle={(checked) => handleToggleChange("showInKiosk", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col">
                    <ToggleSwitch
                      label="Show in POS"
                      isChecked={toggles.showInPOS}
                      onToggle={(checked) => handleToggleChange("showInPOS", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col">
                    <ToggleSwitch
                      label="Show in Online"
                      isChecked={toggles.showInOnline}
                      onToggle={(checked) => handleToggleChange("showInOnline", checked)}
                      themeColor={themeColor}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row g-4">
              <div className="col-md-6">
                <FormInput
                  label="Deal Name *"
                  type="text"
                  value={formState.dealName}
                  onchange={(e) => handleInputChange("dealName", e.target.value)}
                  error={errors.dealName}
                  placeHolder="Enter deal name"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold mb-2">Deal Pricing Type</label>
                <select
                  className="form-select"
                  value={formState.dealPricingType || "CustomPrice"}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormState(prev => ({
                      ...prev,
                      dealPricingType: val,
                      dealPrice: val === "CustomPrice" ? prev.dealPrice : "",
                      dealDiscountPercentage: val === "Discount" ? prev.dealDiscountPercentage : "",
                    }));
                  }}
                  style={{
                    backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0',
                    color: isDarkMode ? '#ffffff' : '#2d3748',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px'
                  }}
                >
                  <option value="CustomPrice">Fixed Price</option>
                  <option value="Discount">Custom Discount %</option>
                  <option value="SlotPricing">Slot Pricing</option>
                </select>
               
              </div>

              {formState.dealPricingType === "CustomPrice" && (
                <div className="col-md-6">
                  <FormInput
                    label={`Deal Price (${getCurrencySymbol(compCurrency)}) *`}
                    type="number"
                    step="0.01"
                    min="0"
                    value={formState.dealPrice}
                    onchange={(e) => handleInputChange("dealPrice", e.target.value)}
                    error={errors.dealPrice}
                    placeHolder="Enter fixed deal price"
                  />
                </div>
              )}

              {formState.dealPricingType === "Discount" && (
                <div className="col-md-6">
                  <FormInput
                    label="Discount Percentage"
                    type="number"
                    min="0"
                    max="100"
                    value={formState.dealDiscountPercentage}
                    onchange={(e) => handleInputChange("dealDiscountPercentage", e.target.value)}
                    placeHolder="0-100"
                  />
                </div>
              )}

              <div className="col-md-12">
                <label className="form-label fw-semibold mb-2">Deal Description</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={formState.dealDescription}
                  onChange={(e) => handleInputChange("dealDescription", e.target.value)}
                  placeholder="Enter deal description"
                  style={{
                    backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                    border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0',
                    color: isDarkMode ? '#ffffff' : '#2d3748',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColor;
                    e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = isDarkMode ? '#404040' : '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div className="col-md-12">
                <div className="row g-4">
                  <div className="col-md-6">
                    <label
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}
                    >
                      Start Date
                    </label>
                    <div style={{
                      padding: '10px',
                      backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                      borderRadius: '10px',
                      border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0'
                    }}>
                      <CustomDateTimePicker
                        value={formState.startDate}
                        onChange={(date) => handleInputChange("startDate", date)}
                        placeholder="Select start date"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label
                      className="form-label fw-semibold mb-2"
                      style={{ color: isDarkMode ? "#f1f5f9" : "#2d3748" }}
                    >
                      End Date
                    </label>
                    <div style={{
                      padding: '10px',
                      backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                      borderRadius: '10px',
                      border: isDarkMode ? '1px solid #404040' : '1px solid #e2e8f0'
                    }}>
                      <CustomDateTimePicker
                        value={formState.endDate}
                        onChange={(date) => handleInputChange("endDate", date)}
                        placeholder="Select end date"
                      />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {!isChildBranch && (
          <div className="card border-0 shadow-sm mb-4" style={{
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : undefined,
            borderRadius: '16px',
            overflow: 'hidden'
          }}>
            <div
              className="card-header border-0"
              style={{
                backgroundColor: themeColor,
                color: 'white',
                padding: '15px 20px',
                fontWeight: 600,
                fontSize: 16,
              }}
            >
              <i className="fas fa-code-branch me-2" />
              Branch Assignment
            </div>
            <div className="card-body" style={{ padding: 25 }}>
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div>
                  <h6 style={{ marginBottom: 5, fontWeight: 600 }}>
                    Assign to Branches
                  </h6>
                  <p style={{ margin: 0, color: isDarkMode ? '#b2bec3' : '#6c757d', fontSize: 14 }}>
                    Select which branches should have access to this deal
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowBranchModal(true)}
                  style={{
                    backgroundColor: themeColor,
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 16px',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  <i className="fas fa-edit me-2" />
                  Manage Branches
                </button>
              </div>
              {selectedBranches.length > 0 ? (
                <div style={{
                  padding: 15,
                  backgroundColor: isDarkMode ? '#1e2124' : '#f8f9fa',
                  borderRadius: 8,
                  border: `1px solid ${themeColor}30`,
                }}>
                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-check-circle me-2" style={{ color: themeColor }} />
                    <span style={{ fontWeight: 500 }}>
                      Assigned to {selectedBranches.length} branch{selectedBranches.length !== 1 ? 'es' : ''}
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {selectedBranches.map((branch) => {
                      const branchId = typeof branch === 'number' ? branch : (branch.CompID || branch.compID);
                      const branchName = typeof branch === 'object'
                        ? (branch.CompName || branch.compName || `Branch ${branchId}`)
                        : `Branch ${branch}`;
                      return (
                        <span
                          key={branchId}
                          className="badge"
                          style={{
                            backgroundColor: `${themeColor}20`,
                            color: themeColor,
                            padding: '6px 12px',
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                        >
                          {branchName}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{
                  padding: 15,
                  backgroundColor: isDarkMode ? '#1e2124' : '#f8f9fa',
                  borderRadius: 8,
                  border: `1px dashed ${themeColor}50`,
                  textAlign: 'center',
                  color: isDarkMode ? '#b2bec3' : '#6c757d',
                }}>
                  <i className="fas fa-info-circle me-2" />
                  No branches assigned yet. Click &quot;Manage Branches&quot; to assign this deal to child branches.
                </div>
              )}
            </div>
          </div>
        )}
      </form>

      <BranchAssignmentSelector
        isOpen={showBranchModal}
        onClose={() => setShowBranchModal(false)}
        selectedBranches={selectedBranches}
        setSelectedBranches={setSelectedBranches}
        productId={null}
        parentCompID={CompID}
        onSubmitSelection={async () => true}
      />
      </GridSectionLoader>
    </div>
  );
};

export default CreateDeal;

